import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { ArrowsClockwise, CheckCircle, XCircle, Database, Cloud, Cpu, WifiHigh } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StatusRow = ({ icon: Icon, label, ok, latency, extra, testid }) => (
  <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40" data-testid={testid}>
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${ok ? 'bg-emerald-500/15 text-emerald-500' : 'bg-red-500/15 text-red-500'}`}>
      <Icon size={20} weight="duotone" />
    </div>
    <div className="flex-1 min-w-0">
      <div className="font-semibold text-sm">{label}</div>
      <div className="text-xs text-muted-foreground truncate">{extra}</div>
    </div>
    <div className="text-right">
      {ok ? (
        <Badge className="bg-emerald-500/15 text-emerald-500 border-emerald-500/30" data-testid={`${testid}-status`}>
          <CheckCircle size={12} weight="fill" className="mr-1" />
          OK
        </Badge>
      ) : (
        <Badge className="bg-red-500/15 text-red-500 border-red-500/30" data-testid={`${testid}-status`}>
          <XCircle size={12} weight="fill" className="mr-1" />
          Falla
        </Badge>
      )}
      {latency > 0 && <div className="text-[10px] text-muted-foreground mt-1 tabular-nums">{latency} ms</div>}
    </div>
  </div>
);

export const HealthPanel = () => {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchHealth = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/health`, { withCredentials: true });
      setHealth(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
    const t = setInterval(fetchHealth, 30 * 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="space-y-5" data-testid="health-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Estado del sistema</h2>
        <button onClick={fetchHealth} className="text-xs text-primary flex items-center gap-1 hover:underline" data-testid="health-refresh">
          <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} />
          Chequear ahora
        </button>
      </div>

      <Card className="p-5 border-border space-y-3">
        <StatusRow
          testid="health-backend"
          icon={Cpu}
          label="Servidor / Backend"
          ok={!!health?.backend?.ok}
          latency={0}
          extra={health?.backend?.server_time ? `Server time: ${health.backend.server_time.slice(0, 19).replace('T', ' ')}` : ''}
        />
        <StatusRow
          testid="health-db"
          icon={Database}
          label="MongoDB"
          ok={!!health?.mongo?.ok}
          latency={health?.mongo?.latency_ms}
          extra={health?.mongo?.ok ? 'Conexión OK' : 'Sin respuesta'}
        />
        <StatusRow
          testid="health-smm"
          icon={Cloud}
          label="Panel SMM"
          ok={!!health?.smm_api?.ok}
          latency={health?.smm_api?.latency_ms}
          extra={
            health?.smm_api?.ok
              ? `Balance: ${health.smm_api.balance?.balance} ${health.smm_api.balance?.currency || ''}`
              : health?.smm_api?.error || 'Sin configurar'
          }
        />
        <StatusRow
          testid="health-frontend"
          icon={WifiHigh}
          label="Frontend (esta página)"
          ok={true}
          latency={0}
          extra={`URL: ${window.location.host}`}
        />
      </Card>

      {health?.smm_api?.balance && (
        <Card className="p-5 border-border" data-testid="health-smm-details">
          <h3 className="font-semibold mb-2">Balance SMM</h3>
          <pre className="text-xs font-mono bg-muted/40 p-3 rounded overflow-x-auto">
            {JSON.stringify(health.smm_api.balance, null, 2)}
          </pre>
        </Card>
      )}
    </div>
  );
};
